import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Button from 'client/components/aria/Button/Button';
import Table from 'client/components/aria/Table';
import { Cell, Column, Row, TableBody, TableHeader } from 'react-stately';

const normaliseIdentifier = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const trimmedValue = String(value).trim();
    return trimmedValue.length ? trimmedValue.toLowerCase() : null;
};

const extractOwnerIdentifiers = (owner) => (
    [
        owner?.organisationId,
        owner?.organisationID,
        owner?.id,
        owner?.dataProviderId,
        owner?.dataProviderID
    ]
        .map(normaliseIdentifier)
        .filter(Boolean)
);

const ensureContactsShape = (contacts = {}) => ({
    business: Array.isArray(contacts.business) ? contacts.business : [],
    emergency: Array.isArray(contacts.emergency) ? contacts.emergency : []
});

const findContactSourceForOwner = (owner, assetOwners = {}) => {
    if (!owner) {
        return null;
    }

    const assetOwnerSources = [
        ...(assetOwners.operationalZone ?? []),
        ...(assetOwners.assetsInAOIOwners ?? [])
    ].filter(Boolean);

    const ownerIdentifiers = extractOwnerIdentifiers(owner);

    if (!ownerIdentifiers.length) {
        return null;
    }

    return assetOwnerSources.find(candidate => {
        const candidateIdentifiers = extractOwnerIdentifiers(candidate);
        return candidateIdentifiers.some(id => ownerIdentifiers.includes(id));
    });
};

const getOwnerDisplayName = (owner, fallbackLabel) => {
    const potentialNames = [
        owner?.name,
        owner?.organisationName,
        owner?.displayName,
        owner?.label,
        owner?.organisationId
    ];

    const displayName = potentialNames.find(value => typeof value === 'string' && value.trim().length);
    return displayName ?? fallbackLabel;
};

const resolveSignupTag = (intl, statusRecord) => {
    const contacted = statusRecord?.contacted;

    if (contacted === 'true' || contacted === true) {
        return {
            className: 'govuk-tag govuk-tag--green',
            label: intl.formatMessage({ id: 'viewEnquiryPage.notificationStatuses.tag.yes' })
        };
    }

    if (contacted === 'false' || contacted === false) {
        return {
            className: 'govuk-tag govuk-tag--red',
            label: intl.formatMessage({ id: 'viewEnquiryPage.notificationStatuses.tag.no' })
        };
    }

    return {
        className: 'govuk-tag govuk-tag--blue',
        label: intl.formatMessage({ id: 'viewEnquiryPage.notificationStatuses.tag.unknown' })
    };
};

const NotificationStatuses = ({ notificationStatuses, assetOwners, onShowContactDetails }) => {
    const intl = useIntl();
    const notAvailableLabel = intl.formatMessage({ id: 'viewEnquiryPage.notification.table.notAvailable' });

    return (
        <div
            aria-label={intl.formatMessage({ id: 'viewEnquiryPage.notificationStatuses.title' })}
            data-testid="notificationStatuses"
        >
            <Table
                aria-label={intl.formatMessage({ id: 'viewEnquiryPage.notificationStatuses.title' })}
                data-testid="notificationStatuses.table"
                className="govuk-!-margin-bottom-0"
            >
                <TableHeader className="govuk-!-margin-bottom-4">
                    <Column>
                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.table.organisations' })}
                    </Column>
                    <Column>
                        {intl.formatMessage({ id: 'viewEnquiryPage.notificationStatuses.table.signedUp' })}
                    </Column>
                    <Column className="govuk-!-text-align-right">
                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.table.contactDetails' })}
                    </Column>
                </TableHeader>
                <TableBody>
                    {notificationStatuses.map((statusRecord, index) => {
                        const ownerKey = String(
                            statusRecord?.organisationId ??
                            statusRecord?.organisationID ??
                            statusRecord?.id ??
                            statusRecord?.dataProviderId ??
                            index
                        );

                        const contactSource = findContactSourceForOwner(statusRecord, assetOwners);
                        const contacts = ensureContactsShape(contactSource?.contacts ?? {});
                        const organisationLabel = getOwnerDisplayName(contactSource ?? statusRecord, notAvailableLabel);
                        const tag = resolveSignupTag(intl, statusRecord);

                        return (
                            <Row key={ownerKey}>
                                <Cell>
                                    <div data-testid={`notificationStatuses.item.${ownerKey}`}>
                                        <span className="govuk-body">
                                            {organisationLabel}
                                        </span>
                                        {typeof statusRecord?.status === 'string' && (
                                            <p className="govuk-hint govuk-!-margin-top-1 govuk-!-margin-bottom-0">
                                                {statusRecord.status}
                                            </p>
                                        )}
                                    </div>
                                </Cell>
                                <Cell>
                                    <strong
                                        className={tag.className}
                                        data-testid={`notificationStatuses.signupTag.${ownerKey}`}
                                    >
                                        {tag.label}
                                    </strong>
                                </Cell>
                                <Cell horzAlign="right">
                                    <Button
                                        variant="secondary"
                                        onPress={() => {
                                            onShowContactDetails({
                                                organisationName: organisationLabel,
                                                contacts,
                                                contacted: statusRecord?.contacted
                                            });
                                        }}
                                        className="govuk-!-margin-top-2 govuk-!-margin-bottom-2"
                                        data-testid={`notificationStatuses.contactDetailsButton.${ownerKey}`}
                                    >
                                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.table.viewContact' })}
                                    </Button>
                                </Cell>
                            </Row>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

NotificationStatuses.propTypes = {
    notificationStatuses: PropTypes.arrayOf(PropTypes.object),
    assetOwners: PropTypes.shape({
        operationalZone: PropTypes.array,
        assetsInAOIOwners: PropTypes.array
    }),
    onShowContactDetails: PropTypes.func.isRequired
};

NotificationStatuses.defaultProps = {
    notificationStatuses: [],
    assetOwners: {
        operationalZone: [],
        assetsInAOIOwners: []
    }
};

export default NotificationStatuses;
